import { useEffect, useState, type FormEvent } from 'react';
import type { EvidenceChainRecord, EvidenceType, EvidenceStatus } from '@stfors/shared-types';
import { Modal } from './Modal';
import { DateInput } from './DateInput';
import { LookupSelect } from './LookupSelect';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { useUpdateEvidence } from '../api/queries/evidence';
import './EditEvidenceModal.css';

interface Props {
  open: boolean;
  evidence: EvidenceChainRecord;
  onClose: () => void;
}

const HEX64 = /^[0-9a-f]{64}$/;

export function EditEvidenceModal({ open, evidence, onClose }: Props): JSX.Element {
  const update = useUpdateEvidence();
  // Overwriting/clearing an existing integrity hash is admin-only; gate the
  // hash field on the same permission the API enforces (evidence.delete).
  const canEditHash = can('evidence.delete', evidence.project_id);

  const [evidenceType, setEvidenceType] = useState<EvidenceType>('other');
  const [evidenceStatus, setEvidenceStatus] = useState<EvidenceStatus>('received');
  const [description, setDescription] = useState('');
  const [serial, setSerial] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [owner, setOwner] = useState('');
  const [client, setClient] = useState('');
  const [collectedFrom, setCollectedFrom] = useState('');
  const [acquiredDate, setAcquiredDate] = useState<number | null>(null);
  const [storagePhysical, setStoragePhysical] = useState('');
  const [storageDigital, setStorageDigital] = useState('');
  const [disposition, setDisposition] = useState('');
  const [hash, setHash] = useState('');
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setEvidenceType(evidence.evidence_type);
    setEvidenceStatus(evidence.evidence_status);
    setDescription(evidence.description);
    setSerial(evidence.serial_number ?? '');
    setMakeModel(evidence.make_model ?? '');
    setOwner(evidence.owner_name ?? '');
    setClient(evidence.client_name ?? '');
    setCollectedFrom(evidence.collected_from ?? '');
    setAcquiredDate(evidence.acquired_date);
    setStoragePhysical(evidence.storage_physical ?? '');
    setStorageDigital(evidence.storage_digital ?? '');
    setDisposition(evidence.disposition ?? '');
    setHash(evidence.hash_value ?? '');
    setTopError(null);
    setFieldErrors({});
  }, [open, evidence]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (description.trim().length === 0) {
      setFieldErrors({ description: 'Description is required' });
      return;
    }
    if (acquiredDate == null) {
      setFieldErrors({ acquired_date: 'Acquired date is required' });
      return;
    }
    const normHash = hash.trim().toLowerCase();
    if (canEditHash && normHash && !HEX64.test(normHash)) {
      setFieldErrors({ hash_value: 'Hash must be 64-char lowercase SHA-256 hex' });
      return;
    }

    try {
      await update.mutateAsync({
        id: evidence.id,
        evidence_type: evidenceType,
        evidence_status: evidenceStatus,
        description: description.trim(),
        serial_number: serial.trim() || null,
        make_model: makeModel.trim() || null,
        owner_name: owner.trim() || null,
        client_name: client.trim() || null,
        collected_from: collectedFrom.trim() || null,
        acquired_date: acquiredDate,
        storage_physical: storagePhysical.trim() || null,
        storage_digital: storageDigital.trim() || null,
        disposition: disposition.trim() || null,
        ...(canEditHash ? { hash_value: normHash || null } : {}),
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setTopError(
          'You don’t have permission for this change. Correcting an existing integrity hash requires admin access.',
        );
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { error?: string; details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const mapped: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) mapped[k] = v[0]!;
          }
          if (Object.keys(mapped).length > 0) {
            setFieldErrors(mapped);
            return;
          }
        }
        setTopError(body?.error ?? 'Please check the fields.');
        return;
      }
      setTopError('Could not save the correction. Try again.');
    }
  };

  const busy = update.isPending;

  return (
    <Modal
      title={`Edit Evidence — ${evidence.chain_of_custody_number}`}
      open={open}
      onClose={onClose}
      busy={busy}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="edit-evidence-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save correction'}
          </button>
        </>
      }
    >
      <form id="edit-evidence-form" className="eem-form" onSubmit={onSubmit} noValidate>
        <p className="eem-note">
          Corrects this entry’s fields. The chain-of-custody timeline is not altered — the change
          is written to the audit log and a correction note is appended to the timeline.
        </p>

        <div className="eem-row">
          <label className="eem-field">
            <span>Type</span>
            <LookupSelect
              category="evidence_type"
              value={evidenceType}
              onChange={setEvidenceType}
              className="form-input"
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Status</span>
            <LookupSelect
              category="evidence_status"
              value={evidenceStatus}
              onChange={setEvidenceStatus}
              className="form-input"
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Acquired date</span>
            <DateInput value={acquiredDate} onChange={setAcquiredDate} disabled={busy} />
            {fieldErrors['acquired_date'] && (
              <span className="field-error">{fieldErrors['acquired_date']}</span>
            )}
          </label>
        </div>

        <label className="eem-field eem-field-wide">
          <span>Description</span>
          <textarea
            className="form-input eem-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={10_000}
            disabled={busy}
          />
          {fieldErrors['description'] && <span className="field-error">{fieldErrors['description']}</span>}
        </label>

        <div className="eem-row">
          <label className="eem-field">
            <span>Serial</span>
            <input
              type="text"
              className="form-input"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Make / model</span>
            <input
              type="text"
              className="form-input"
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </label>
        </div>

        <div className="eem-row">
          <label className="eem-field">
            <span>Owner</span>
            <input
              type="text"
              className="form-input"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Client</span>
            <input
              type="text"
              className="form-input"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Collected from</span>
            <input
              type="text"
              className="form-input"
              value={collectedFrom}
              onChange={(e) => setCollectedFrom(e.target.value)}
              maxLength={500}
              disabled={busy}
            />
          </label>
        </div>

        <div className="eem-row">
          <label className="eem-field">
            <span>Physical storage</span>
            <input
              type="text"
              className="form-input"
              value={storagePhysical}
              onChange={(e) => setStoragePhysical(e.target.value)}
              maxLength={500}
              disabled={busy}
            />
          </label>
          <label className="eem-field">
            <span>Digital storage</span>
            <input
              type="text"
              className="form-input"
              value={storageDigital}
              onChange={(e) => setStorageDigital(e.target.value)}
              maxLength={1000}
              disabled={busy}
            />
          </label>
        </div>

        <label className="eem-field eem-field-wide">
          <span>Disposition</span>
          <input
            type="text"
            className="form-input"
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            maxLength={1000}
            disabled={busy}
          />
        </label>

        {canEditHash && (
          <label className="eem-field eem-field-wide">
            <span>Integrity hash (SHA-256)</span>
            <input
              type="text"
              className="form-input eem-hash"
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              placeholder="64-char hex (lowercase) — leave blank to clear"
              maxLength={64}
              disabled={busy}
            />
            <span className="eem-hash-warn">
              Changing an existing hash is an audited admin override and is recorded in the audit
              log and chain-of-custody timeline.
            </span>
            {fieldErrors['hash_value'] && <span className="field-error">{fieldErrors['hash_value']}</span>}
          </label>
        )}

        {topError && <p className="eem-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
